import { getPortalSupabase } from "@/lib/portalSupabase";
import { useLocation } from "wouter";
import { LogOut, ArrowLeftRight, Settings, MessageSquare, CircleHelp, FolderOpen } from "lucide-react";
import { useChatUnreadCount } from "@/components/portal/ClientChat";
import { Button } from "@/components/ui/button";
import { useConfirm } from "@/lib/confirmContext";

interface PortalLayoutProps {
  children: React.ReactNode;
  accountName?: string;
}

export function PortalLayout({ children, accountName }: PortalLayoutProps) {
  const [, setLocation] = useLocation();
  const isMultiAccount = localStorage.getItem("portal_multi_account") === "true";
  const isPreview = localStorage.getItem("portal_preview") === "true";
  const chatUnread = useChatUnreadCount();
  const askConfirm = useConfirm();

  function handleExitPreview() {
    const returnAccountId = localStorage.getItem("portal_preview_return");
    localStorage.removeItem("portal_preview");
    localStorage.removeItem("portal_preview_return");
    localStorage.removeItem("portal_account_id");
    setLocation(returnAccountId ? `/accounts/${returnAccountId}` : "/accounts");
  }

  async function handleSignOut() {
    if (!(await askConfirm({
      title: "Sign out of the portal?",
      body: "You will need your sign-in link to get back in.",
      confirmLabel: "Sign out",
    }))) return;
    const supabase = await getPortalSupabase();
    await supabase.auth.signOut();
    localStorage.removeItem("portal_account_id");
    localStorage.removeItem("portal_multi_account");
    setLocation("/portal/login");
  }

  function handleSwitchAccount() {
    localStorage.removeItem("portal_account_id");
    setLocation("/portal/pick-account");
  }

  return (
    <div className="min-h-screen bg-background">
      {isPreview && (
        <div className="bg-amber-500 text-amber-950 px-4 py-2 text-sm font-medium flex items-center justify-between">
          <span>👁 Staff preview — viewing the portal as {accountName ?? "this client"} (read-only)</span>
          <button onClick={handleExitPreview} className="underline font-semibold hover:opacity-80">
            Exit preview
          </button>
        </div>
      )}
      <header className="border-b bg-card px-4 md:px-6 py-3 flex items-center justify-between">
        <div
          className="flex items-center gap-3 cursor-pointer"
          role="link"
          tabIndex={0}
          title="Back to your projects"
          onClick={() => setLocation("/portal")}
          onKeyDown={(e) => { if (e.key === "Enter") setLocation("/portal"); }}
        >
          <img
            src="/carpenter-company-logo.png"
            alt="Carpenter &amp; Company CPAs"
            className="h-8 w-auto"
            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
          />
          <div>
            <span className="font-semibold text-sm">Carpenter &amp; Company, CPAs</span>
            {accountName && (
              <span className="text-muted-foreground text-sm ml-2 truncate max-w-[160px] sm:max-w-none">— {accountName}</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={() => setLocation("/portal")}>
            <FolderOpen className="w-4 h-4 mr-1" />
            Projects
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setLocation("/portal/messages")} className="relative">
            <MessageSquare className="w-4 h-4 mr-1" />
            Messages
            {chatUnread > 0 && (
              <span className="absolute -top-0.5 -right-0.5 inline-flex items-center justify-center rounded-full bg-primary text-primary-foreground text-[10px] font-bold min-w-[16px] h-[16px] px-1">
                {chatUnread}
              </span>
            )}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setLocation("/portal/help")}>
            <CircleHelp className="w-4 h-4 mr-1" />
            Help
          </Button>
          {!isPreview && isMultiAccount && (
            <Button variant="ghost" size="sm" onClick={handleSwitchAccount}>
              <ArrowLeftRight className="w-4 h-4 mr-1" />
              Switch account
            </Button>
          )}
          {!isPreview && (
            <>
              <Button variant="ghost" size="sm" onClick={() => setLocation("/portal/settings")}>
                <Settings className="w-4 h-4 mr-1" />
                Settings
              </Button>
              <Button variant="ghost" size="sm" onClick={handleSignOut}>
                <LogOut className="w-4 h-4 mr-1" />
                Sign out
              </Button>
            </>
          )}
        </div>
      </header>
      <main className="max-w-4xl mx-auto px-4 md:px-6 py-8">
        {children}
      </main>
    </div>
  );
}
